import React from "react";
import { useNavigate, useParams } from "react-router-dom";

import { AvatarProfile, ButtonStandard, Chip, Text } from "@performyard/py-components";
import type { ICellRendererParams } from "@performyard/py-components";

import { formatCurrencyFull } from "../../utils";
import { CycleBudgetOverviewTableRow } from "../CycleOverview/utils";
import { pluralize } from "../SharedComponents/utils";

export const ManagerNameCellRenderer = (params: ICellRendererParams<CycleBudgetOverviewTableRow>) => {
	const row = params.data;
	if (!row) return null;

	return (
		<div className="flex h-full items-center gap-2">
			<div className="flex min-w-0 flex-col justify-center gap-0.5">
				<AvatarProfile employeeId={row.managerId} employeeName={row.managerName} noLink={true}></AvatarProfile>
			</div>
		</div>
	);
};

export const ActingManagerNameCellRenderer = (params: ICellRendererParams<CycleBudgetOverviewTableRow>) => {
	const row = params.data;
	if (!row) return null;

	if (!row?.actingManagerId || row?.actingManagerId === row.managerId) {
		return null;
	}

	return (
		<div className="flex h-full items-center gap-2">
			<div className="flex min-w-0 flex-col justify-center gap-0.5">
				<Chip
					label="Acting Manager"
					employeeId={row.actingManagerId}
					employeeName={row.actingManagerName ?? ""}
					color="neutral"
					size={24}
					employeeTextSize={"caption"}
				/>
			</div>
		</div>
	);
};

export const BudgetStatusCellRenderer = (params: ICellRendererParams<CycleBudgetOverviewTableRow>) => {
	const row = params.data;
	if (!row) return null;

	const statusColorMap = {
		Submitted: "yellow",
		"Not Submitted": "neutral",
		Approved: "green",
	};

	return (
		<div className="flex h-full items-center gap-2">
			<div className="flex min-w-0 flex-col justify-center gap-0.5">
				<Chip label={row.status} color={statusColorMap[row.status]} size={24} />
			</div>
		</div>
	);
};

export const DirectReportCountCellRenderer = (params: ICellRendererParams<CycleBudgetOverviewTableRow>) => {
	const row = params.data;
	if (!row) return null;

	return (
		<div className="flex h-full items-center gap-2">
			<div className="flex min-w-0 flex-col justify-center gap-0.5">
				<Text size="caption" weight="regular" color="neutral-7">
					{row?.managerDirectReportCount}{" "}
					{pluralize(row?.managerDirectReportCount ?? 0, "Direct Report", "Direct Reports")}
				</Text>
			</div>
		</div>
	);
};

export const BudgetCellRenderer = (params: ICellRendererParams<CycleBudgetOverviewTableRow>) => {
	const row = params.data;
	if (!row) return null;

	return (
		<div className="flex h-full items-center gap-2">
			<div className="flex min-w-0 flex-col justify-center gap-0.5">
				<Text size="caption" weight="regular" color="neutral-7">
					{formatCurrencyFull(row?.budget)}
				</Text>
			</div>
		</div>
	);
};

const statusMap = {
	Approved: "approved",
	"Not Submitted": "not-submitted",
	Submitted: "submitted",
};
export const ViewBudgetButtonCellRenderer = (params: ICellRendererParams<CycleBudgetOverviewTableRow>) => {
	const row = params.data;
	const navigate = useNavigate();
	const { cycleId } = useParams<{ cycleId: string }>();
	if (!row) return null;

	const rowStatus = statusMap[row.status];

	return (
		<div className="flex h-full w-full items-center justify-center">
			<div className="flex w-[70px] flex-col justify-center gap-0.5">
				<ButtonStandard
					color="neutral"
					variant="outline"
					size="small"
					onClick={() => {
						navigate(
							`/compensation/cycles/${cycleId}/budget-reviews?search=${encodeURIComponent(row.managerName)}&quickFilter=${rowStatus}`
						);
					}}
				>
					View
				</ButtonStandard>
			</div>
		</div>
	);
};
